import { useState, useEffect } from "react";
import axios, { API } from "../../api";

/**
 * Phase 28B — Creator Studio Add-on purchase + management modal.
 *
 * $9.99/mo recurring. Unlocks:
 *   - Live screen-recording during outbound calls
 *   - Download MP3 of past calls + transcript
 *   - 15-min call cap (vs 10)
 *   - Future: ffmpeg.wasm clip editor + one-tap social share
 */
export function CreatorStudioModal({ onClose, user, onPurchaseStarted }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const isActive = !!user?.creator_studio_active;
  const isPremium = !!user?.has_premium;
  const cancelPending = !!user?.creator_studio_cancel_at_period_end;
  const expiresAt = user?.creator_studio_expires_at;

  const handlePurchase = async () => {
    setBusy(true);
    setError("");
    try {
      const res = await axios.post(`${API}/payment/creator-studio/checkout`, {
        origin_url: window.location.origin,
      });
      const url = res.data?.url;
      if (!url) throw new Error("Couldn't start checkout");
      if (onPurchaseStarted) onPurchaseStarted();
      window.location.href = url;
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't start checkout — try again in a moment.");
      setBusy(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm("Cancel Creator Studio? You'll keep access until the end of your billing period.")) return;
    setBusy(true);
    setError("");
    try {
      const res = await axios.post(`${API}/payment/creator-studio/cancel`);
      alert(res.data?.message || "Canceled. Access continues until period end.");
      onClose();
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't cancel — try again later.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose} data-testid="creator-studio-overlay">
      <div className="creator-studio-modal" onClick={(e) => e.stopPropagation()}>
        <div className="journal-header">
          <h2>🎬 Creator Studio</h2>
          <button className="journal-close" onClick={onClose} data-testid="creator-studio-close">×</button>
        </div>

        <p className="cs-tagline">For YouTubers, TikTokers, streamers — and anyone who wants to post the call.</p>

        <ul className="cs-features">
          <li>📸 Live screen-record while Aura is on the call</li>
          <li>🎙️ Download MP3 of any past call</li>
          <li>📝 Full transcript with speaker labels</li>
          <li>⏱️ Extended <strong>15-minute</strong> call cap (up from 10)</li>
          <li><em>Soon:</em> Trim & export clips + one-tap social share</li>
        </ul>

        <div className="cs-price-row" data-testid="cs-price-row">
          <span className="cs-price">$9.99</span>
          <span className="cs-price-period">/ month</span>
        </div>

        {error && <div className="error-message" data-testid="cs-error">{error}</div>}

        {!isPremium && (
          <div className="lock-banner" data-testid="cs-not-premium-banner">
            🔒 Creator Studio requires an active Aura premium tier. Upgrade first.
          </div>
        )}

        {isActive ? (
          <>
            <div className="cs-active-banner" data-testid="cs-active-banner">
              ✅ Creator Studio is active
              {expiresAt && (
                <span className="cs-expiry">
                  {cancelPending ? " — ends" : " — renews"} {new Date(expiresAt).toLocaleDateString()}
                </span>
              )}
            </div>
            {!cancelPending && (
              <button
                className="init-btn cs-cancel-btn"
                onClick={handleCancel}
                disabled={busy}
                data-testid="cs-cancel-btn"
              >
                {busy ? "Working..." : "Cancel auto-renew"}
              </button>
            )}
          </>
        ) : (
          <button
            className="init-btn cs-cta-btn"
            onClick={handlePurchase}
            disabled={!isPremium || busy}
            data-testid="cs-purchase-btn"
          >
            {busy ? "Opening checkout..." : "🎬 Unlock Creator Studio"}
          </button>
        )}

        <p className="cs-fine-print">Cancel anytime. Tax calculated at checkout per your billing address.</p>
      </div>
    </div>
  );
}
